import { Order } from "@/api/bot/types";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getHoursSince } from "@/utils/date";
import { applyMinMaxFilter } from "@/utils/filters";
import { createContext, useContext, useState, ReactNode } from "react";

export type OrdersSortField =
  | "token"
  | "type"
  | "price"
  | "outflow"
  | "inflow"
  | "value"
  | "realizedPNL"
  | "realizedROI"
  | "time";

export interface OrderSort {
  sortField: OrdersSortField;
  sortType: "asc" | "desc";
}

export interface OrderFilters {
  type?: "buy" | "sell";
  priceMin?: number;
  priceMax?: number;
  valueMin?: number;
  valueMax?: number;
  timeMin?: number;
  timeMax?: number;
}

interface OrdersFilterContextProps {
  filters: OrderFilters;
  searchInput: string;
  setSearchInput: (query: string) => void;
  setFilters: (filters: OrderFilters) => void;
  updateFilter: (key: keyof OrderFilters, value: number) => void;
  clearFilters: () => void;
  filterOrders: (orders: Order[]) => Order[];
  defaultFilters: OrderFilters;
  sort: OrderSort;
  handleSortChange: (sortField: OrdersSortField) => void;
  selectedToken?: string;
  handleTokenChange: (token: string | undefined) => void;
}

const defaultFilters: OrderFilters = {
  type: undefined,
  priceMin: undefined,
  priceMax: undefined,
  valueMin: undefined,
  valueMax: undefined,
  timeMin: undefined,
  timeMax: undefined,
};

const OrdersFilterContext = createContext<OrdersFilterContextProps | null>(
  null
);

export const OrdersFilterProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<OrderFilters>(defaultFilters);

  const [selectedToken, setSelectedToken] = useState<string | undefined>(
    undefined
  );

  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebouncedValue(searchInput, 300);

  const [sort, setSort] = useState<OrderSort>({
    sortField: "time",
    sortType: "desc" as "asc" | "desc",
  });

  const updateFilter = (key: keyof OrderFilters, value: number) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const handleSortChange = (sortField: OrdersSortField) => {
    setSort((prevSort) => {
      if (prevSort.sortField === sortField) {
        return {
          ...prevSort,
          sortType: prevSort.sortType === "asc" ? "desc" : "asc",
        };
      }
      return { sortField, sortType: "desc" };
    });
  };

  const handleTokenChange = (token: string | undefined) => {
    if (token && token !== selectedToken) {
      setSelectedToken(token);
    } else {
      setSelectedToken(undefined);
    }
  };

  const filterOrders = (orders: Order[]) => {
    const filtered = orders.filter((order) => {
      if (
        searchQuery &&
        !order.tokenInfo?.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) &&
        !order.tokenInfo?.symbol
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) &&
        !order.tokenInfo?.address
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      if (filters.type && order.type !== filters.type) {
        return false;
      }

      if (
        selectedToken &&
        order.tokenInfo?.address?.toLowerCase() !== selectedToken.toLowerCase()
      ) {
        return false;
      }

      return (
        applyMinMaxFilter(
          Number(order.price),
          filters.priceMin,
          filters.priceMax
        ) &&
        applyMinMaxFilter(
          Number(order.value),
          filters.valueMin,
          filters.valueMax
        ) &&
        applyMinMaxFilter(
          getHoursSince(order.createdAt, true),
          filters.timeMin,
          filters.timeMax
        )
      );
    });

    return filtered.sort((a, b) => {
      let aValue: number | string = 0;
      let bValue: number | string = 0;

      switch (sort.sortField) {
        case "token":
          aValue = a.tokenInfo?.name.toLowerCase() || "";
          bValue = b.tokenInfo?.name.toLowerCase() || "";
          break;
        case "type":
          aValue = a.type || "";
          bValue = b.type || "";
          break;
        case "price":
          aValue = Number(a.price ?? 0);
          bValue = Number(b.price ?? 0);
          break;
        case "outflow":
          aValue = Number(a.outflow ?? 0);
          bValue = Number(b.outflow ?? 0);
          break;
        case "inflow":
          aValue = Number(a.inflow ?? 0);
          bValue = Number(b.inflow ?? 0);
          break;
        case "realizedPNL":
          aValue = a.expectedPnl ?? 0;
          bValue = b.expectedPnl ?? 0;
          break;
        case "realizedROI":
          aValue = a.expectedRoi ?? 0;
          bValue = b.expectedRoi ?? 0;
          break;
        case "value":
          aValue = a.value ?? 0;
          bValue = b.value ?? 0;
          break;
        case "time":
          aValue = getHoursSince(b.createdAt) ?? 0;
          bValue = getHoursSince(a.createdAt) ?? 0;
          break;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.sortType === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sort.sortType === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });
  };

  return (
    <OrdersFilterContext.Provider
      value={{
        filters,
        searchInput,
        setSearchInput,
        setFilters,
        updateFilter,
        clearFilters,
        filterOrders,
        defaultFilters,
        sort,
        handleSortChange,
        selectedToken,
        handleTokenChange,
      }}
    >
      {children}
    </OrdersFilterContext.Provider>
  );
};

export const useOrdersFilter = () => {
  const context = useContext(OrdersFilterContext);
  if (!context)
    throw new Error("useOrdersFilter must be used within a Provider");
  return context;
};
